import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, toQuery } from '../client';
import type { Availability, Hall, HallFilters, Quote, Review, HallEquipment } from '../types';

export const hallKeys = {
  all: ['halls'] as const,
  list: (filters: HallFilters) => ['halls', 'list', filters] as const,
  detail: (id: string) => ['halls', 'detail', id] as const,
  availability: (id: string, date: string) => ['halls', id, 'availability', date] as const,
  quote: (id: string, start: number, end: number) => ['halls', id, 'quote', start, end] as const,
};

export function useHalls(filters: HallFilters) {
  return useQuery({
    queryKey: hallKeys.list(filters),
    queryFn: () => api<{ halls: Hall[] }>('/halls' + toQuery(filters)),
    select: (data) => data.halls,
  });
}

export function useHall(id: string) {
  return useQuery({
    queryKey: hallKeys.detail(id),
    queryFn: () =>
      api<{
        hall: Hall;
        reviews: Review[];
        similar: Hall[];
        equipment?: HallEquipment[];
        /** The building this hall is in, with its other halls. */
        property?: { id: number; name: string; halls: Hall[] } | null;
      }>(`/halls/${id}`),
  });
}

export function useAvailability(id: string, date: string) {
  return useQuery({
    queryKey: hallKeys.availability(id, date),
    queryFn: () => api<Availability>(`/halls/${id}/availability?date=${date}`),
    // A hold placed by anyone else changes this; don't serve it stale.
    staleTime: 0,
    /**
     * ...and staleTime alone was not enough. It marks the cache stale, it does
     * not go and ask: a visitor sitting on the slot grid triggers no remount and
     * no focus event, so nothing refetched at all. Holds are released by a
     * server-side sweep after HOLD_MINUTES, which meant an abandoned checkout
     * kept its slot greyed out until someone reloaded the page by hand. The same
     * silence hid other people's new bookings and a manager's fresh blocks.
     *
     * Polling is what makes the release visible. React Query does not poll a
     * hidden tab unless asked to, so a backgrounded page stays quiet and the
     * refetch below covers the return.
     */
    refetchInterval: 30_000,
    // Overrides the app-wide default: this is the one screen where coming back
    // to the tab must not show a grid that stopped being true minutes ago.
    refetchOnWindowFocus: true,
  });
}

/**
 * Busy windows across a run of days (inclusive), for the several-days
 * calendar: one request for the month on screen rather than one per day.
 */
export function useAvailabilityRange(id: string, from: string, to: string, enabled = true) {
  return useQuery({
    queryKey: [...hallKeys.availability(id, from), to] as const,
    queryFn: () => api<Availability>(`/halls/${id}/availability?date=${from}&to=${to}`),
    enabled,
    staleTime: 0,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });
}

export function useQuote(id: string, start: number | null, end: number | null) {
  return useQuery({
    queryKey: hallKeys.quote(id, start ?? 0, end ?? 0),
    queryFn: () => api<Quote>(`/halls/${id}/quote?start=${start}&end=${end}`),
    enabled: start !== null && end !== null,
  });
}

export function useSendEnquiry(hallId: string) {
  return useMutation({
    mutationFn: (body: {
      name: string;
      phone: string;
      email: string;
      event_date: number | null;
      guests: number | null;
      message: string;
    }) => api<{ ok: true; id: number }>(`/halls/${hallId}/enquiries`, { method: 'POST', body }),
  });
}

export function useLeaveReview(hallId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { rating: number; comment: string }) =>
      api<{ ok: true }>(`/halls/${hallId}/reviews`, { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: hallKeys.detail(String(hallId)) }),
  });
}
